"use client"

import { create } from "zustand"
import { useEffect } from "react"

type Theme = "dark" | "light" | "system"

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const STORAGE_KEY = "ui-theme"

export const useThemeStore = create<ThemeState>((set) => ({
  theme: "system",
  setTheme: (theme: Theme) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, theme)
    }
    set({ theme })
  },
}))

const updateDOMTheme = (theme: Theme) => {
  const root = window.document.documentElement
  root.classList.remove("light", "dark")

  const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  const effectiveTheme = theme === "system" ? systemTheme : theme

  root.classList.add(effectiveTheme)
  root.style.colorScheme = effectiveTheme
}

export function ThemeSyncer() {
  const theme = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedTheme = localStorage.getItem(STORAGE_KEY) as Theme | null
      if (storedTheme) {
        setTheme(storedTheme)
      }
    }
  }, [setTheme])

  useEffect(() => {
    updateDOMTheme(theme)
  }, [theme])

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = () => {
      if (theme === "system") {
        updateDOMTheme("system")
      }
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [theme])

  return null
}
